// Cash Flow Chart Component
import {
  AreaChart,
  Area,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from 'recharts';
import { formatCurrency } from '../utils/calculations';

interface CashFlowTrendChartProps {
  data: Array<{
    date: string;
    income: number;
    expense: number;
    balance: number;
  }>;
}

export function CashFlowTrendChart({ data }: CashFlowTrendChartProps) {
  const CustomTooltip = ({ active, payload }: any) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-card border border-border rounded-lg p-3 shadow-lg">
          <p className="text-sm font-medium text-foreground mb-2">
            {new Date(payload[0].payload.date).toLocaleDateString('fr-DZ', {
              day: 'numeric',
              month: 'long',
              year: 'numeric',
            })}
          </p>
          <div className="space-y-1">
            <p className="text-sm text-emerald-600 dark:text-emerald-400">
              Recettes: {formatCurrency(payload[0].payload.income)}
            </p>
            <p className="text-sm text-red-600 dark:text-red-400">
              Dépenses: {formatCurrency(payload[0].payload.expense)}
            </p>
            <p className="text-sm text-blue-600 dark:text-blue-400 font-semibold">
              Solde: {formatCurrency(payload[0].payload.balance)}
            </p>
          </div>
        </div>
      );
    }
    return null;
  };

  return (
    <div className="w-full h-80 bg-card border border-border rounded-lg p-6">
      <h3 className="text-lg font-semibold text-foreground mb-4">
        <span className="font-arabic">تطور الرصيد</span>
        <span className="text-muted-foreground mx-2">/</span>
        <span>Évolution du solde</span>
      </h3>
      
      <ResponsiveContainer width="100%" height="100%">
        <AreaChart data={data}>
          <defs>
            <linearGradient id="colorBalance" x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.3} />
              <stop offset="95%" stopColor="#3b82f6" stopOpacity={0} />
            </linearGradient>
          </defs>
          <CartesianGrid strokeDasharray="3 3" className="stroke-border" />
          <XAxis
            dataKey="date"
            tickFormatter={(date) => new Date(date).toLocaleDateString('fr-DZ', { day: '2-digit', month: '2-digit' })}
            className="text-muted-foreground"
          />
          <YAxis
            tickFormatter={(value) => `${(value / 1000).toFixed(0)}k`}
            className="text-muted-foreground"
          />
          <Tooltip content={<CustomTooltip />} />
          <Legend />
          <Area
            type="monotone"
            dataKey="balance"
            name="Solde"
            stroke="#3b82f6"
            strokeWidth={2}
            fillOpacity={1}
            fill="url(#colorBalance)"
          />
        </AreaChart>
      </ResponsiveContainer>
    </div>
  );
}

interface IncomeVsExpenseChartProps {
  data: Array<{
    date: string;
    income: number;
    expense: number;
  }>;
}

export function IncomeVsExpenseChart({ data }: IncomeVsExpenseChartProps) {
  const CustomTooltip = ({ active, payload }: any) => {
    if (active && payload && payload.length) {
      const net = payload[0].payload.income - payload[0].payload.expense;
      return (
        <div className="bg-card border border-border rounded-lg p-3 shadow-lg">
          <p className="text-sm font-medium text-foreground mb-2">
            {new Date(payload[0].payload.date).toLocaleDateString('fr-DZ', {
              day: 'numeric',
              month: 'long',
              year: 'numeric',
            })}
          </p>
          <div className="space-y-1">
            <p className="text-sm text-emerald-600 dark:text-emerald-400">
              Recettes: {formatCurrency(payload[0].payload.income)}
            </p>
            <p className="text-sm text-red-600 dark:text-red-400">
              Dépenses: {formatCurrency(payload[0].payload.expense)}
            </p>
            <p className={`text-sm font-semibold ${net >= 0 ? 'text-emerald-600 dark:text-emerald-400' : 'text-red-600 dark:text-red-400'}`}>
              Net: {formatCurrency(net)}
            </p>
          </div>
        </div>
      );
    }
    return null;
  };

  return (
    <div className="w-full h-80 bg-card border border-border rounded-lg p-6">
      <h3 className="text-lg font-semibold text-foreground mb-4">
        <span className="font-arabic">الإيرادات مقابل المصاريف</span>
        <span className="text-muted-foreground mx-2">/</span>
        <span>Recettes vs Dépenses</span>
      </h3>
      
      <ResponsiveContainer width="100%" height="100%">
        <BarChart data={data}>
          <CartesianGrid strokeDasharray="3 3" className="stroke-border" />
          <XAxis
            dataKey="date"
            tickFormatter={(date) => new Date(date).toLocaleDateString('fr-DZ', { day: '2-digit', month: '2-digit' })}
            className="text-muted-foreground"
          />
          <YAxis
            tickFormatter={(value) => `${(value / 1000).toFixed(0)}k`}
            className="text-muted-foreground"
          />
          <Tooltip content={<CustomTooltip />} />
          <Legend />
          <Bar dataKey="income" name="Recettes" fill="#10b981" radius={[8, 8, 0, 0]} />
          <Bar dataKey="expense" name="Dépenses" fill="#ef4444" radius={[8, 8, 0, 0]} />
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}
